import { useMemo } from 'react'

import { QuickGuiddeType, QuizCtaType, QuizOutcome, QuizProgress } from 'app/types'
import {
    hasAnonymousQuizAccess,
    shouldShowIdentificationGate,
    useIsAnonymousQuizEnabled,
} from 'components/QuizIdentification'
import { useAuth, useQuizIdentity } from 'hooks'

import { getDisplayableQuiz, isQuizComplete } from './quiz-helpers'
import { useQuizStatuses } from './use-quiz-statuses'

export type VisibleQuiz = {
    quiz: QuizCtaType
    showAt: number
}

export type QuizSummary = Omit<QuizProgress, 'result'>

export type QuizPlaybackInfo = {
    quizzes: Array<VisibleQuiz>
    hasQuiz: boolean
    requiredQuizIds: Array<string>
    priorPassedIds: Set<string>
    needsIdentification: boolean
    quizSummaries: Array<QuizSummary>
    outcomeByQuizId: Record<string, QuizOutcome>
    isReady: boolean
}

// Anonymous viewers see a quiz only when anonymous quiz is allowed and the quiz's identification mode
// and the viewer's identity permit it; otherwise quiz is authenticated-only.
// For now we show the quiz whenever an enabled one is present for a logged-in user — there is no
// plan-entitlement gate yet.
// @TODO(GD-19589): gate on the org's Stigg `hasQuiz` entitlement. It is not yet exposed to embed —
// `feature-quiz` must first be mapped into the servicesUsage doc (MAP_LIMITS_FROM_STIGG) and returned
// by `feature-flags-public` (like `canHideWatermarkEmbed`), then AND it into `isQuizEnabled` so quizzes
// disappear on downgrade.
export const useVisibleQuizzes = (playbook: QuickGuiddeType, allowAnonymousQuiz = true): QuizPlaybackInfo => {
    const { steps, slicingSuggestion } = playbook
    const { user } = useAuth()
    const { isAnonymousQuizEnabled, isLoading: isAnonymousQuizFlagLoading } = useIsAnonymousQuizEnabled(
        playbook.uploadedByOrgId,
    )
    const { identity, isDismissed: isPlaybookDismissed } = useQuizIdentity(playbook.uploadedByOrgId)
    const isDismissed = isPlaybookDismissed(playbook.id)

    const isAnonymousViewer = user?.isAnonymous === true && isAnonymousQuizEnabled && allowAnonymousQuiz
    const hasQuizPermission = user?.isAnonymous === false || isAnonymousViewer
    const needsIdentification = shouldShowIdentificationGate({ isAnonymousViewer, identity, isDismissed })

    const hasEnabledQuiz = useMemo(() => steps.some(step => step.cta?.ctaType === 'quiz' && step.cta.enabled), [steps])

    const isQuizEnabled = hasQuizPermission && hasEnabledQuiz

    const { statusByQuizId, outcomeByQuizId, isLoading } = useQuizStatuses(playbook.id, isQuizEnabled)

    const isViewerResolved = user !== null && !(user.isAnonymous && allowAnonymousQuiz && isAnonymousQuizFlagLoading)
    const isReady = !hasEnabledQuiz || (isViewerResolved && !isLoading)

    return useMemo(() => {
        if (!isQuizEnabled || isLoading)
            return {
                quizzes: [],
                hasQuiz: false,
                requiredQuizIds: [],
                priorPassedIds: new Set<string>(),
                needsIdentification: false,
                quizSummaries: [],
                outcomeByQuizId: {},
                isReady,
            }

        const slicing = slicingSuggestion ?? []
        const quizzes: Array<VisibleQuiz> = []
        const priorPassedIds = new Set<string>()
        const quizSummaries: Array<QuizSummary> = []

        steps.forEach((step, index) => {
            if (step.cta?.ctaType !== 'quiz' || !step.cta.enabled) return

            const quiz = getDisplayableQuiz(step.cta)
            if (!quiz) return
            if (isAnonymousViewer && !hasAnonymousQuizAccess(quiz, { identity, isDismissed })) return

            quizSummaries.push({
                id: quiz.id,
                isRequired: quiz.requirePassToComplete,
                questionCount: quiz.questions.length,
            })

            const passed = isQuizComplete(statusByQuizId[quiz.id])
            if (passed) priorPassedIds.add(quiz.id)

            const showAt = slicing[index]?.end
            if (typeof showAt === 'number' && !passed) quizzes.push({ quiz, showAt })
        })

        return {
            quizzes,
            hasQuiz: !isAnonymousViewer || quizSummaries.length > 0,
            requiredQuizIds: quizSummaries.filter(summary => summary.isRequired).map(summary => summary.id),
            priorPassedIds,
            needsIdentification,
            quizSummaries,
            outcomeByQuizId,
            isReady,
        }
    }, [
        isQuizEnabled,
        isLoading,
        isReady,
        outcomeByQuizId,
        slicingSuggestion,
        steps,
        statusByQuizId,
        isAnonymousViewer,
        identity,
        isDismissed,
        needsIdentification,
    ])
}
